/* eslint-disable react/jsx-no-bind, react/jsx-max-depth, max-lines-per-function, react/jsx-sort-props, complexity */
import * as Yup from "yup";
import { Alert, Button, Col, FloatingLabel, Form, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useCallback, useState } from "react";
import { Formik } from "formik";
import { useMutation } from "react-query";

import LwcErrorBoundary from "../../../components/LwcErrorBoundary";

import PasswordStrengthCard from "./PasswordStrengthCard";
import PasswordWithShow from "../../../components/PasswordWithShow";
import RecaptchaField from "../../../components/RecaptchaField/RecaptchaField";

import { decodeEntity, passwordValidationSchema, setLocalCredentials } from "../../../helpers/authHelpers";
import registerUserQuery from "./registrationMutation";
import registrationFormModel from "./registrationFormModel";
import { useAuth } from "../../../services/wp.auth.service";
import { useNotifications } from "../../../components/NotificationsContext";

const {
	formId,
	formField: {
		firstName,
		lastName,
		email,
		password,
		passwordConfirm
	}
} = registrationFormModel;

const schema = Yup.object().shape({
	[firstName.name]: Yup.string().required(`${firstName.requiredErrorMsg}`),
	[lastName.name]: Yup.string().required(`${lastName.requiredErrorMsg}`),
	[email.name]: Yup.string().email(`${email.invalidErrorMsg}`).
		required(`${email.requiredErrorMsg}`),
	human: Yup.string().matches("human", { message: "You must be human to proceed." }).
		required("You must be human to proceed."),
	terms: Yup.array(Yup.string("agree")).length(1, "You must agree to the terms").
		required("You must agree to the terms"),
	...passwordValidationSchema()
});

const initialValues = {
	email: "",
	firstName: "",
	human: "",
	lastName: "",
	password: "",
	passwordConfirm: "",
	term: []
};

const AccountInfoForm = () => {
	const navigate = useNavigate();
	const { refetchUser } = useAuth();
	const { addNotification } = useNotifications();

	// eslint-disable-next-line no-unused-vars
	const [newPassword, setNewPassword] = useState(null);
	const [formErrors, setFormErrors] = useState([]);

	const {
		isLoading,
		mutate
	} = registerUserQuery(useMutation, {
		onError: (error) => {
			addNotification({
				message: error.message,
				type: "danger"
			});
		},
		onSuccess: async ({ data }) => {
			// eslint-disable-next-line no-console
			console.log(data);
			if (data.errors) {
				setFormErrors(data.errors);
			} else if (data?.data.registerUser?.user) {
				const { email: userEmail, jwtAuthToken, jwtRefreshToken } = data.data.registerUser.user;
				setLocalCredentials(userEmail, jwtAuthToken, jwtRefreshToken);
				await refetchUser();
				navigate("/account/register-success");
			} else {
				setFormErrors([{ message: "Unknown error occurred." }]);
			}
		}
	});
	const register = useCallback((values) => {
		setFormErrors([]);
		mutate({
			...values,
			username: values.email.replace("+", "")
		});
	}, []);

	return (
		<LwcErrorBoundary>
			<Formik
				initialValues={initialValues}
				onSubmit={register}
				validationSchema={schema}
			>
				{({
					errors,
					handleChange,
					handleSubmit,
					handleBlur,
					touched,
					values
				}) => {
					const recaptchaCallback = useCallback((value) => {
						if (value) {
							handleChange({ target: { name: "human",
								value } });
						}
					}, []);
					return (
						<Form className="mb-4" id={formId} noValidate onSubmit={handleSubmit}>
							<FloatingLabel
								className="mb-4"
								controlId={firstName.name}
								label={firstName.label}
							>
								<Form.Control
									// eslint-disable-next-line react/jsx-no-leaked-render
									isInvalid={touched.firstName && !!errors.firstName}
									// eslint-disable-next-line react/jsx-no-leaked-render
									isValid={touched.firstName && !errors.firstName}
									name="firstName"
									onBlur={handleBlur}
									onChange={handleChange}
									placeholder={firstName.label}
									required
									value={values.firstName}
								/>
								{errors.firstName ? <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback> : null}
							</FloatingLabel>
							{errors.firstName &&
								touched.firstName ? (
									<Form.Control.Feedback type="invalid">
										{errors.firstName}
									</Form.Control.Feedback>
								) : null}

							<FloatingLabel className="mb-4" controlId={lastName.name} label={lastName.label}>
								<Form.Control
									// eslint-disable-next-line react/jsx-no-leaked-render
									isInvalid={touched.lastName && !!errors.lastName}
									// eslint-disable-next-line react/jsx-no-leaked-render
									isValid={touched.lastName && !errors.lastName}
									name="lastName"
									onBlur={handleBlur}
									onChange={handleChange}
									placeholder={lastName.label}
									required
									value={values.lastName}
								/>
								{errors.lastName ? <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback> : null}
							</FloatingLabel>
							{errors.lastName && touched.lastName ? (
								<Form.Control.Feedback type="invalid">
									{errors.lastName}
								</Form.Control.Feedback>
							) : null}

							<FloatingLabel className="mb-4" controlId={email.name} label={email.label}>
								<Form.Control
									// eslint-disable-next-line react/jsx-no-leaked-render
									isInvalid={touched.email && !!errors.email}
									// eslint-disable-next-line react/jsx-no-leaked-render
									isValid={touched.email && !errors.email}
									name="email"
									onBlur={handleBlur}
									onChange={handleChange}
									placeholder={email.label}
									required
									type="email"
								/>
								{errors.email ? <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback> : null}
							</FloatingLabel>

							<Row className="mb-4">
								<Col className="mb-4 mb-md-0" md={6} sm={12}>
									<PasswordWithShow
									// eslint-disable-next-line react/jsx-no-leaked-render
										isInvalid={touched.password && !!errors.password}
										// eslint-disable-next-line react/jsx-no-leaked-render
										isValid={touched.password && !errors.password}
										name="password"
										onBlur={handleBlur}
										onChange={(evt) => {
											handleChange(evt);
											setNewPassword(evt.target.value);
										}}
										placeholder={password.label}
										required
										value={values.password}
									/>

									{errors.password ? (
										<Form.Control.Feedback
											className="d-block"
											type="invalid"
										>
											{errors.password}
										</Form.Control.Feedback>) : null}

									<FloatingLabel className="my-4" controlId={passwordConfirm.name} label={passwordConfirm.label}>
										<Form.Control
											// eslint-disable-next-line react/jsx-no-leaked-render
											isInvalid={touched.passwordConfirm && !!errors.passwordConfirm}
											// eslint-disable-next-line react/jsx-no-leaked-render
											isValid={touched.passwordConfirm && !errors.passwordConfirm}
											name="passwordConfirm"
											onBlur={handleBlur}
											onChange={handleChange}
											placeholder={passwordConfirm.label}
											required
											type="password"
											value={values.passwordConfirm}
										/>
										{errors.passwordConfirm
											? <Form.Control.Feedback type="invalid">{errors.passwordConfirm}</Form.Control.Feedback> : null}
									</FloatingLabel>

									<LwcErrorBoundary>
										<Form.Group controlId="terms">
											<Form.Check
												type="checkbox"
											>
												<Form.Check.Input
													isInvalid={!!errors.terms}
													// eslint-disable-next-line react/jsx-no-leaked-render
													isValid={touched.terms && !errors.terms}
													name="terms"
													onBlur={handleBlur}
													onChange={handleChange}
													required
													type="checkbox"
													value="agree"
												/>
												<Form.Check.Label className="d-inline ms-2">
													{/* eslint-disable-next-line max-len */}
													I agree to the <a href="/terms-of-service" target="_blank">Terms of Service</a>, <a href="https://www.lifewithcancer.org/privacy-policy/" rel="noreferrer" target="_blank">Privacy Policy</a>, <a href="/general-waiver" target="_blank">General Waiver</a>, and the <a href="/physical-activity-waiver" target="_blank">Physical Activity Waiver</a>
												</Form.Check.Label>
												{errors.terms ? <Form.Control.Feedback type="invalid">{errors.terms}</Form.Control.Feedback> : null}
											</Form.Check>
										</Form.Group>
									</LwcErrorBoundary>
								</Col>

								<Col md={6} sm={12}>
									<PasswordStrengthCard password={newPassword} />
								</Col>
							</Row>

							{errors.human
								? <Form.Control.Feedback className="d-block" type="invalid">{errors.human}</Form.Control.Feedback> : null}

							<RecaptchaField onSuccess={recaptchaCallback} />

							{formErrors.length ? (
								<Alert variant="danger">
									{formErrors.map((error, idx) => (
										<div
											dangerouslySetInnerHTML={{ __html: decodeEntity(error.message) }}
											/* eslint-disable-next-line react/no-array-index-key */
											key={idx}
										/>))}
								</Alert>) : null}

							<div className="d-flex justify-content-between">
								<Button as={Link} to="/login" variant="link">&larr; Back to Login</Button>
								<Button disabled={isLoading} type="submit">
									{isLoading ? <Spinner animation="border" size="sm" /> : "Complete Sign Up →"}
								</Button>
							</div>
						</Form>
					);
				}}
			</Formik>
		</LwcErrorBoundary>
	);

};

export default AccountInfoForm;
